<template>
  <ListComponent title="Bulk Upload">
    <template #actions>
      <v-btn color="primary" outlined depressed class="mr-2" @click="getUploadList()">
        <v-icon left small>mdi-refresh</v-icon>
        Refresh
      </v-btn>
      <v-btn color="primary" depressed @click="openUploadDialog">
        <v-icon left small>mdi-tray-arrow-up</v-icon>
        New Upload
      </v-btn>
    </template>

    <template #list>
      <v-row class="ma-0">
        <v-col cols="12" md="4" class="pa-2">
          <v-card outlined class="fill-height">
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="blue lighten-5" size="40" class="mr-3">
                  <v-icon color="blue darken-2">mdi-file-document-outline</v-icon>
                </v-avatar>
                <span class="overline grey--text">Total Uploads</span>
              </div>
              <div class="text-h4 font-weight-bold mt-3">
                {{ summary.totalUploads }}
              </div>
              <div class="caption grey--text">All time</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" class="pa-2">
          <v-card outlined class="fill-height">
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="green lighten-5" size="40" class="mr-3">
                  <v-icon color="green darken-2">mdi-check-circle-outline</v-icon>
                </v-avatar>
                <span class="overline grey--text">Rows Processed</span>
              </div>
              <div class="text-h4 font-weight-bold mt-3">
                {{ summary.rowsProcessed }}
              </div>
              <div class="caption grey--text">All successful</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" class="pa-2">
          <v-card outlined class="fill-height">
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="amber lighten-5" size="40" class="mr-3">
                  <v-icon color="amber darken-3">mdi-clock-outline</v-icon>
                </v-avatar>
                <span class="overline grey--text">Last Upload</span>
              </div>
              <div class="text-h4 font-weight-bold mt-3">
                {{ summary.lastUploadRelative }}
              </div>
              <div class="caption grey--text">{{ summary.lastUploadDate }}</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="4" class="pa-2 d-flex align-center">
          <v-text-field
            label="Search On Enter"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            @click:clear="onEnter()"
            v-on:keyup.enter="onEnter"
            v-model="filters.search"
          />
        </v-col>

        <v-col cols="12" class="pa-2">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 380px)"
            class="ag-theme-alpine"
            id="bulkUploadGrid"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            @columnMoved="resizeGrid"
            @columnVisible="resizeGrid"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="uploadList"
          ></AgGridVue>
        </v-col>
      </v-row>

      <VendorSubHireUploadDialog
        v-model="showUploadDialog"
        @refreshList="getUploadList"
      />
    </template>
  </ListComponent>
</template>

<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import VendorSubHireUploadDialog from "@/components/ModuleBased/dialogForms/VendorSubHireUploadDialog.vue";
import VendorSubHireUploadViewButton from "@/components/AgGridComponents/VendorSubHireUploadViewButton.vue";

export default {
  components: {
    ListComponent,
    AgGridVue,
    agColumnHeader: CustomHeader,
    VendorSubHireUploadDialog,
    VendorSubHireUploadViewButton,
  },

  data() {
    return {
      filters: {},
      showUploadDialog: false,

      summary: {
        totalUploads: 0,
        rowsProcessed: 0,
        lastUploadRelative: "-",
        lastUploadDate: "-",
      },

      uploadList: [],

      headers: [
        {
          headerName: "Upload ID",
          field: "id",
          sortable: true,
          minWidth: 110,
          cellClass: ["primary--text font-weight-bold"],
          valueFormatter: (params) => (params.value ? `UPL-${params.value}` : ""),
        },
        {
          headerName: "File Name",
          field: "file_name",
          tooltipField: "file_name",
          sortable: true,
          minWidth: 220,
        },
        {
          headerName: "Uploaded On",
          field: "uploaded_on",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Draft Jobs",
          field: "total_draft_subhirejobs",
          sortable: true,
          minWidth: 110,
        },
        {
          headerName: "Job Breakdown",
          field: "job_breakdown",
          minWidth: 260,
          valueGetter: (params) => {
            if (!params.data) return [];
            const breakdown = [];
            if (params.data.pending) {
              breakdown.push({ label: `${params.data.pending} Pending`, status: "Pending" });
            }
            if (params.data.partially_accepted) {
              breakdown.push({
                label: `${params.data.partially_accepted} Partial`,
                status: "Partially Accepted",
              });
            }
            if (params.data.fully_accepted) {
              breakdown.push({
                label: `${params.data.fully_accepted} Accepted`,
                status: "Fully Accepted",
              });
            }
            if (params.data.rejected) {
              breakdown.push({ label: `${params.data.rejected} Rejected`, status: "Rejected" });
            }
            return breakdown;
          },
          cellRenderer: (params) => {
            const chips = (params.value || [])
              .map((item) => {
                const colors = {
                  Pending: "background:#fef3c7;color:#92400e;",
                  "Partially Accepted": "background:#dbeafe;color:#1e40af;",
                  "Fully Accepted": "background:#dcfce7;color:#166534;",
                  Rejected: "background:#fee2e2;color:#991b1b;",
                };
                const style = colors[item.status] || "background:#eee;color:#333;";
                return `<span style="display:inline-block;${style}border-radius:12px;padding:2px 10px;font-size:12px;font-weight:600;margin-right:4px;margin-bottom:2px;">${item.label}</span>`;
              })
              .join("");
            return `<div style="white-space:normal;line-height:1.8;display:flex;align-items:center;flex-wrap:wrap;height:100%;padding:6px 0;">${chips}</div>`;
          },
          autoHeight: true,
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 130,
          cellRenderer: (params) => {
            const labels = {
              pending: "Pending",
              in_process: "In Process",
              completed: "Completed",
            };
            const colors = {
              pending: "background:#fef3c7;color:#92400e;",
              in_process: "background:#dbeafe;color:#1e40af;",
              completed: "background:#dcfce7;color:#166534;",
            };
            const style = colors[params.value] || "background:#eee;color:#333;";
            const label = labels[params.value] || params.value;
            return `<span style="display:inline-block;${style}border-radius:12px;padding:4px 12px;font-size:12px;font-weight:700;line-height:16px;white-space:nowrap;">${label}</span>`;
          },
        },
        {
          headerName: "Last Updated",
          field: "modified",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Actions",
          field: "actions",
          minWidth: 110,
          pinned: "right",
          cellRendererFramework: "VendorSubHireUploadViewButton",
        },
      ],

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 46,
      },
    };
  },

  computed: {
    context() {
      return { parentComponent: this };
    },
  },

  methods: {
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    onEnter() {
      this.getUploadList();
    },
    openUploadDialog() {
      this.showUploadDialog = true;
    },
    openUploadDetails(row) {
      this.$router.push(`/app/transporter/bulk-upload/details/${row.id}`);
    },
    updateSummary() {
      this.summary.totalUploads = this.uploadList.length;
      this.summary.rowsProcessed = this.uploadList.reduce(
        (total, upload) => total + (upload.total_draft_subhirejobs || 0),
        0
      );

      if (this.uploadList.length === 0) {
        this.summary.lastUploadRelative = "-";
        this.summary.lastUploadDate = "-";
        return;
      }

      const lastUpload = [...this.uploadList].sort(
        (a, b) => new Date(b.created) - new Date(a.created)
      )[0];
      this.summary.lastUploadRelative = this.$globals.formatDate(lastUpload.created, true);
      this.summary.lastUploadDate = this.$globals.formatDate(lastUpload.created);
    },
    getUploadList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        ...params,
        ...this.filters,
      };
      this.$api.vendorSubHireUpload
        .getVendorSubHireUploadList(params)
        .then((res) => {
          this.uploadList = res.data;
          this.updateSummary();
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },

  beforeMount() {
    this.getUploadList();
  },
};
</script>
